import React, { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/authContext';
import { doCreateUserWithEmailAndPassword } from '../../../firebase/auth';
import Logo from '../../common/Logo';
import VectorBackground from '../../common/VectorBackground';

const Register = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isRegistering, setIsRegistering] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const { userLoggedIn, appSettings } = useAuth();

    if (!appSettings.registrationEnabled) {
        return <Navigate to={'/login'} replace={true} />;
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');
        if (password !== confirmPassword) {
            setErrorMessage("Passwords do not match.");
            return;
        }
        if(!isRegistering) {
            setIsRegistering(true);
            try {
                await doCreateUserWithEmailAndPassword(email, password);
            } catch (error) {
                let friendlyMessage = 'An unexpected error occurred during registration.';
                switch (error.code) {
                    case 'auth/email-already-in-use':
                        friendlyMessage = 'An account with this email address already exists.';
                        break;
                    case 'auth/weak-password':
                        friendlyMessage = 'The password is too weak. Please use a stronger password (at least 6 characters).';
                        break;
                    case 'auth/operation-not-allowed':
                        friendlyMessage = 'Registration is currently disabled by the administrator.';
                        break;
                    default:
                        friendlyMessage = `Registration Error: ${error.message}`;
                }
                setErrorMessage(friendlyMessage);
                setIsRegistering(false);
            }
        }
    };

    return (
        <div className="relative w-full h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200">
            {userLoggedIn && (<Navigate to={'/home'} replace={true} />)}
            
            <VectorBackground />

            <main className="relative z-10 w-full max-w-md p-8 space-y-6 bg-white/10 dark:bg-gray-900/10 backdrop-blur-lg border border-white/20 dark:border-white/10 rounded-2xl shadow-2xl">
                <div className="text-center">
                    <Logo />
                    <h2 className="mt-6 text-2xl font-bold text-gray-900 dark:text-gray-100">
                        Create an Account
                    </h2>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        Join to start managing your projects
                    </p>
                </div>
                
                <form onSubmit={onSubmit} className="space-y-4">
                    <div>
                        <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Email</label>
                        <input
                            type="email"
                            autoComplete='email'
                            required
                            value={email} onChange={(e) => { setEmail(e.target.value) }}
                            className="w-full mt-2 px-4 py-2 text-gray-700 dark:text-gray-200 bg-white/20 dark:bg-gray-800/20 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-300"
                        />
                    </div>

                    <div>
                        <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Password</label>
                        <input
                            disabled={isRegistering}
                            type="password"
                            autoComplete='new-password'
                            required
                            value={password} onChange={(e) => { setPassword(e.target.value) }}
                            className="w-full mt-2 px-4 py-2 text-gray-700 dark:text-gray-200 bg-white/20 dark:bg-gray-800/20 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-300"
                        />
                    </div>

                    <div>
                        <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Confirm Password</label>
                        <input
                            disabled={isRegistering}
                            type="password"
                            autoComplete='off'
                            required
                            value={confirmPassword} onChange={(e) => { setConfirmPassword(e.target.value) }}
                            className="w-full mt-2 px-4 py-2 text-gray-700 dark:text-gray-200 bg-white/20 dark:bg-gray-800/20 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-300"
                        />
                    </div>

                    {errorMessage && (
                        <span className='text-red-500 font-bold text-sm text-center block'>{errorMessage}</span>
                    )}

                    <button
                        type="submit"
                        disabled={isRegistering}
                        className={`w-full px-4 py-3 text-white font-semibold rounded-lg transition-colors duration-300 ${isRegistering ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                    >
                        {isRegistering ? 'Creating Account...' : 'Sign Up'}
                    </button>
                    
                    <p className="text-sm text-center text-gray-600 dark:text-gray-400">
                        Already have an account?{' '}
                        <Link to={'/login'} className="font-semibold text-blue-600 hover:underline dark:text-blue-400">
                            Sign in
                        </Link>
                    </p>
                </form>
            </main>
        </div>
    );
}

export default Register;
